/**
 * services/haptics.js — thin wrapper around navigator.vibrate.
 *
 * Mobile-only (Android Chrome / Edge / Samsung; iOS Safari ignores vibrate
 * entirely — there's no Web API equivalent). Desktop browsers also no-op.
 * We don't try to fall back to anything: if the device can vibrate, great;
 * if not, the action is silently a no-op so callers don't need to guard.
 *
 * Patterns kept short so they don't clash with messaging cadence:
 *   tap        - 10ms — single soft tap (send confirm)
 *   notify     - [0, 30, 40, 30] — short staccato (inbound message)
 *   react      - [0, 12, 18, 12] — light double-tap (emoji reaction)
 *
 * The `0` lead in the array form is required by the spec when the first
 * value is a delay — it tells the device "vibrate immediately".
 *
 * Disabled when the user has muted notification sound (via the same
 * preference store realtime.js owns) — most users who silence the inbox
 * also don't want the desk to buzz.
 */

const PATTERNS = Object.freeze({
  tap:    10,
  notify: [0, 30, 40, 30],
  react:  [0, 12, 18, 12],
});

function _enabled() {
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') {
    return false;
  }
  // Respect the same mute preference as the notification sound — agents
  // who silence the inbox typically don't want the buzz either.
  if (localStorage.getItem('inbox_v2_sound_muted') === 'true') return false;
  return true;
}

export function haptic(kind = 'tap') {
  if (!_enabled()) return;
  const pattern = PATTERNS[kind] ?? PATTERNS.tap;
  try { navigator.vibrate(pattern); } catch { /* spec says no-throw, but be safe */ }
}

export function hapticTap()    { haptic('tap'); }
export function hapticNotify() { haptic('notify'); }
export function hapticReact()  { haptic('react'); }
